// Small mixer controls shared by the band's mixer and the standalone metronome.
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { shellHost } from '@/components/shellHost';
import { Check, Search, X } from 'lucide-react';
import { useStore } from '@/state/store';
import { levelMax, type MixFader } from '@/audio/levels';
import {
  groupSoundOptions,
  placeMenu,
  searchSoundOptions,
  type SoundMenuSearch,
  type SoundOption,
} from './soundMenu';
import { touchFirst } from './touchFirst';
import './MixerControls.css';

/** One setting as a row of buttons inside a channel's Settings. */
export function SegmentedChoice<T extends string>({
  label,
  value,
  options,
  onChange,
  testid,
  disabled,
  className,
}: {
  label: string;
  /** Null lights nothing (the bands being edited disagree). */
  value: T | null;
  /** An icon is drawn above its label; the label stays the button's name. */
  options: readonly { id: T; label: string; icon?: ReactNode }[];
  onChange: (value: T) => void;
  testid: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div
      className={className ? `segmented ${className}` : 'segmented'}
      role="group"
      aria-label={label}
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={value === option.id ? 'active' : ''}
          aria-pressed={value === option.id}
          data-testid={`${testid}-${option.id}`}
          disabled={disabled}
          onClick={() => onChange(option.id)}
        >
          {option.icon ? (
            <>
              <span className="segmented-icon" aria-hidden="true">
                {option.icon}
              </span>
              <span>{option.label}</span>
            </>
          ) : (
            option.label
          )}
        </button>
      ))}
    </div>
  );
}

export function LevelFader({
  fader,
  label,
  vertical = false,
}: {
  fader: MixFader;
  label: string;
  vertical?: boolean;
}) {
  const level = useStore((s) => s.channelLevels[fader]);
  const setChannelLevel = useStore((s) => s.setChannelLevel);
  const pct = Math.round(level * 100);
  const max = Math.round(levelMax(fader) * 100);
  return (
    <div
      className={`mixer-level${vertical ? ' mixer-level-vertical' : ''}`}
      style={
        vertical
          ? ({
              '--mixer-fill': `${(pct / max) * 100}%`,
              '--mixer-reference': `${100 / max}`,
            } as CSSProperties)
          : undefined
      }
    >
      {/* The 100% mark, only where the fader goes past it (not the master's top). */}
      {vertical && max > 100 && <span className="mixer-level-reference" aria-hidden="true" />}
      <input
        type="range"
        min={0}
        max={max}
        step={5}
        value={pct}
        onChange={(e) => setChannelLevel(fader, Number(e.target.value) / 100)}
        aria-label={`${label} level`}
        aria-orientation={vertical ? 'vertical' : undefined}
        aria-valuetext={`${pct}%${pct === 100 ? ' · reference balance' : ''}`}
        data-testid={`level-${fader}`}
      />
      <output>{pct}%</output>
    </div>
  );
}

/** A text trigger with a roomy menu outside the mixer's scrolling drawer. */
export function MixerSoundControl({
  sound,
  label,
  value,
  options,
  onChange,
  disabled,
  testid,
  compact,
  search,
  keepOpen,
  className,
}: {
  sound: string;
  label: string;
  value: string;
  options: readonly SoundOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  testid: string;
  /** A mixer strip's name: plain text, as the name reads where it cannot change. */
  compact?: boolean;
  /** A search field over a long list (the drum loops). */
  search?: SoundMenuSearch;
  /** A pick leaves the menu open, so the next one is a press away: drum loops
   * are tried one after another while the band plays (user, Oct 2026). */
  keepOpen?: boolean;
  /** More classes for the trigger (the select look where a select stood). */
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<CSSProperties>({
    top: 0,
    left: 0,
    maxHeight: 360,
    opacity: 0,
  });
  const trigger = useRef<HTMLButtonElement>(null);
  const anchorPosition = useRef<{ top: number; left: number } | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  // The height the menu opened at. A searchable menu keeps it while the search
  // narrows the list, so the field does not move under the person typing.
  const contentHeight = useRef(0);
  const menuId = useId();
  const typeahead = useRef({ text: '', time: 0 });
  // A press on the trigger while the menu is open moves focus off the menu
  // first, and that blur closes it before the click arrives, so the click
  // alone would read a closed menu and open it again.
  const pressedWhileOpen = useRef(false);
  // Whether there is a search, not the object: an option list written inline
  // must not re-run the opening layout (and refocus the field) on every render.
  const searchable = search !== undefined;
  const shown = search ? searchSoundOptions(options, query) : options;
  const groups = groupSoundOptions(shown);
  const openMenu = () => {
    setQuery('');
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  };
  // Beside the trigger, inside what the screen shows: measured on opening, and
  // placed again when the visible screen changes under it, as a phone's
  // keyboard does when the search field takes focus.
  const place = useCallback(
    (measure: boolean) => {
      const anchorElement = trigger.current;
      const panelElement = panel.current;
      const listElement = list.current;
      if (!anchorElement || !panelElement || !listElement) return;
      const anchor = anchorElement.getBoundingClientRect();
      anchorPosition.current = { top: anchor.top, left: anchor.left };
      if (measure)
        contentHeight.current =
          panelElement.offsetHeight - listElement.clientHeight + listElement.scrollHeight;
      const viewport = window.visualViewport;
      const placement = placeMenu(
        anchor,
        {
          top: viewport?.offsetTop ?? 0,
          left: viewport?.offsetLeft ?? 0,
          width: viewport?.width ?? window.innerWidth,
          height: viewport?.height ?? window.innerHeight,
        },
        contentHeight.current,
        searchable ? { maxHeight: 520, maxWidth: 400 } : { maxHeight: 360, maxWidth: 300 },
      );
      setPosition({
        top: placement.top,
        left: placement.left,
        width: placement.width,
        ...(searchable
          ? { height: Math.min(contentHeight.current, placement.maxHeight) }
          : { maxHeight: placement.maxHeight }),
        fontFamily: getComputedStyle(anchorElement).fontFamily,
      });
    },
    [searchable],
  );
  useLayoutEffect(() => {
    if (!open || !list.current) return;
    place(true);
    const selected =
      list.current.querySelector<HTMLButtonElement>('[aria-checked="true"]') ??
      list.current.querySelector<HTMLButtonElement>('[role="menuitemradio"]');
    // Type to search at once, except where focusing the field would raise a
    // phone's keyboard over the list.
    if (searchable && field.current && !touchFirst()) field.current.focus({ preventScroll: true });
    else selected?.focus({ preventScroll: true });
    if (selected) {
      // A row near the start of its section opens under that section's
      // heading; any other sits mid-list, under the heading that has stuck to
      // the top. Centred near a boundary, the section before's heading stuck
      // over its last row and read as an empty section.
      const section = selected.closest<HTMLElement>('[role="group"]');
      const half = list.current.clientHeight / 2;
      list.current.scrollTop = Math.max(
        0,
        section && selected.offsetTop - section.offsetTop < half
          ? section.offsetTop
          : selected.offsetTop - half,
      );
    }
    typeahead.current = { text: '', time: 0 };
  }, [open, place, searchable]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (
        !panel.current?.contains(event.target as Node) &&
        !trigger.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    const follow = () => place(false);
    const scroll = (event: Event) => {
      if (event.target instanceof Node && panel.current?.contains(event.target)) return;
      // Clicking a scrolled-into-view trigger can deliver its scroll event
      // after the menu opens. It is still anchored correctly in that case.
      const anchor = trigger.current?.getBoundingClientRect();
      if (
        anchor &&
        anchorPosition.current &&
        Math.abs(anchor.top - anchorPosition.current.top) < 1 &&
        Math.abs(anchor.left - anchorPosition.current.left) < 1
      )
        return;
      // The page moved the trigger. While a search is being typed the menu
      // follows it (a phone scrolls the page to show the field above its
      // keyboard); otherwise the menu closes.
      if (field.current && document.activeElement === field.current) place(false);
      else setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', scroll, true);
    window.visualViewport?.addEventListener('resize', follow);
    window.visualViewport?.addEventListener('scroll', follow);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', scroll, true);
      window.visualViewport?.removeEventListener('resize', follow);
      window.visualViewport?.removeEventListener('scroll', follow);
    };
  }, [open, place]);
  const row = (option: SoundOption) => (
    <button
      key={option.id}
      type="button"
      role="menuitemradio"
      data-value={option.id}
      tabIndex={-1}
      aria-checked={value === option.id}
      onClick={(event) => {
        onChange(option.id);
        if (!keepOpen) close();
        // A tap leaves the row it picked highlighted, not the one picked
        // before (Safari and a phone do not focus a pressed button).
        else if (event.detail > 0) event.currentTarget.focus({ preventScroll: true });
      }}
    >
      <Check
        size={17}
        aria-hidden="true"
        style={{ visibility: value === option.id ? 'visible' : 'hidden' }}
      />
      <span>{option.label}</span>
    </button>
  );
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`band-mixer-sound-control${compact ? ' band-mixer-sound-compact' : ''}${
          className ? ` ${className}` : ''
        }`}
        title={sound}
        aria-label={`${label}: ${sound}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        data-testid={testid}
        data-value={value}
        onPointerDown={() => {
          pressedWhileOpen.current = open;
        }}
        onClick={() => {
          const wasOpen = pressedWhileOpen.current || open;
          pressedWhileOpen.current = false;
          if (wasOpen) setOpen(false);
          else openMenu();
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) openMenu();
          }
        }}
      >
        <span>{sound}</span>
      </button>
      {open &&
        createPortal(
          <div
            ref={panel}
            className={`band-mixer-sound-menu${search ? ' band-mixer-sound-searchable' : ''}`}
            style={position}
            onBlur={(event) => {
              // Focus going somewhere outside closes the menu. A press on a
              // row in a browser that does not focus buttons blurs to nothing
              // and must not: a press outside closes it through pointerdown.
              const next = event.relatedTarget;
              if (next instanceof Node && !event.currentTarget.contains(next)) setOpen(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                close();
                return;
              }
              if (event.key === 'Tab') {
                close();
                return;
              }
              const items = [
                ...(list.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ??
                  []),
              ];
              if (event.target === field.current) {
                // From the field, the arrows step into the list, and Enter
                // takes the first match.
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  const checked = items.find(
                    (item) => item.getAttribute('aria-checked') === 'true',
                  );
                  (event.key === 'ArrowDown' ? (checked ?? items[0]) : items.at(-1))?.focus();
                } else if (event.key === 'Enter' && query.trim()) {
                  event.preventDefault();
                  items[0]?.click();
                }
                return;
              }
              const current = items.indexOf(document.activeElement as HTMLButtonElement);
              const printable =
                event.key.length === 1 &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey &&
                event.key !== ' ';
              if (field.current && (printable || event.key === 'Backspace')) {
                // Typing in the list types into the search.
                event.preventDefault();
                const key = event.key;
                setQuery((text) => (key === 'Backspace' ? text.slice(0, -1) : text + key));
                field.current.focus();
                return;
              }
              let next: number | undefined;
              if (event.key === 'ArrowDown') next = (current + 1) % items.length;
              if (event.key === 'ArrowUp') {
                if (current === 0 && field.current) {
                  event.preventDefault();
                  field.current.focus();
                  return;
                }
                next = (current - 1 + items.length) % items.length;
              }
              if (event.key === 'Home') next = 0;
              if (event.key === 'End') next = items.length - 1;
              if (printable) {
                const now = Date.now();
                typeahead.current = {
                  text:
                    (now - typeahead.current.time < 700 ? typeahead.current.text : '') +
                    event.key.toLowerCase(),
                  time: now,
                };
                next = items.findIndex((item) =>
                  item.textContent?.trim().toLowerCase().startsWith(typeahead.current.text),
                );
              }
              if (next !== undefined && next >= 0) {
                event.preventDefault();
                items[next]?.focus();
              }
            }}
          >
            {search && (
              <div className="band-mixer-sound-search">
                <Search size={17} aria-hidden="true" />
                <input
                  ref={field}
                  type="search"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    if (list.current) list.current.scrollTop = 0;
                  }}
                  aria-label={search.label}
                  aria-controls={menuId}
                  placeholder={`${search.label}…`}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="search"
                  data-testid={`${testid}-search`}
                />
                {query && (
                  <button
                    type="button"
                    className="band-mixer-sound-clear"
                    aria-label="Clear search"
                    onClick={() => {
                      setQuery('');
                      field.current?.focus();
                    }}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
            {search && shown.length === 0 && (
              <p className="band-mixer-sound-empty" role="status">
                {search.empty}
              </p>
            )}
            <div
              ref={list}
              id={menuId}
              role="menu"
              aria-label={label}
              className="band-mixer-sound-list"
            >
              {groups.map((group, index) =>
                group.name === undefined ? (
                  <Fragment key={`rows-${index}`}>{group.options.map(row)}</Fragment>
                ) : (
                  <div key={group.name} role="group" aria-labelledby={`${menuId}-${index}`}>
                    <div
                      id={`${menuId}-${index}`}
                      className="band-mixer-sound-group"
                      aria-hidden="true"
                    >
                      {group.name}
                    </div>
                    {group.options.map(row)}
                  </div>
                ),
              )}
            </div>
          </div>,
          shellHost(),
        )}
    </>
  );
}
